import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, CircularProgress, Alert, Collapse,
  LinearProgress, Chip, Button, Select, MenuItem, TextField, InputLabel, FormControl
} from '@mui/material';
import { Delete, Link as LinkIcon, Visibility, Build, ExpandLess, ExpandMore, Close, Refresh, PlayArrow } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import toast from 'react-hot-toast';

const ProjectDatasets = () => {
  const { projectId } = useParams();
  const { setSelectedProjectId } = useAuth();
  const [linked, setLinked] = useState([]);
  const [publicUploads, setPublicUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [previewTable, setPreviewTable] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showWrangling, setShowWrangling] = useState(false);
  const [wranglingTable, setWranglingTable] = useState(null);
  const [wranglingColumns, setWranglingColumns] = useState([]);
  const [wranglingAction, setWranglingAction] = useState('sort');
  const [wranglingConfig, setWranglingConfig] = useState({ sortCol: '', sortDir: 'asc', newColName: '', formula: '', col1: '', col2: '', separator: '_', dateCol: '', extractPart: 'year' });
  const [wranglingResult, setWranglingResult] = useState(null);
  const [wranglingLoading, setWranglingLoading] = useState(false);

  useEffect(() => {
    if (projectId) {
      setSelectedProjectId(parseInt(projectId));
      fetchData();
    }
  }, [projectId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [linkedRes, publicRes] = await Promise.all([
        api.get(`/api/v1/tables/project/${projectId}/datasets`),
        api.get('/api/v1/tables/public')
      ]);
      setLinked(linkedRes.data);
      setPublicUploads(publicRes.data);
    } catch (err) {
      setError('Failed to load datasets');
    } finally {
      setLoading(false);
    }
  };

  const handleLink = async (uploadId) => {
    try {
      await api.post(`/api/v1/tables/project/${projectId}/link/${uploadId}`);
      toast.success('Dataset linked to project');
      fetchData();
    } catch (err) {
      toast.error('Link failed');
    }
  };

  const handleUnlink = async (uploadId) => {
    if (!window.confirm('Remove this dataset from the project? It will remain in the public library.')) return;
    try {
      await api.delete(`/api/v1/tables/project/${projectId}/unlink/${uploadId}`);
      toast.success('Unlinked');
      fetchData();
    } catch (err) {
      toast.error('Unlink failed');
    }
  };

  const handlePreview = async (id) => {
    if (previewTable === id) {
      setPreviewTable(null);
      setPreviewData(null);
      return;
    }
    setPreviewLoading(true);
    setPreviewTable(id);
    try {
      const token = localStorage.getItem('access_token');
      const res = await api.get(`/api/v1/tables/${id}/preview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreviewData(res.data);
    } catch (err) {
      toast.error('Failed to load preview');
      setPreviewTable(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleOpenWrangling = async (tableId, columns) => {
    setWranglingTable(tableId);
    setWranglingColumns(columns);
    setShowWrangling(true);
    setWranglingResult(null);
  };

  const loadWranglingColumns = async (tableId) => {
  try {
    const token = localStorage.getItem('access_token');
    const res = await api.get(`/api/v1/tables/${tableId}/preview?rows=1`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setWranglingColumns(res.data.columns || []);
  } catch (err) {
    toast.error('Failed to load columns');
    setWranglingColumns([]);
  }
};

  const handleWranglingApply = async () => {
    if (!wranglingTable) return;
    setWranglingLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const res = await api.post(`/api/v1/tables/${wranglingTable}/wrangle`, {
        action: wranglingAction,
        config: wranglingConfig
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWranglingResult(res.data);
      toast.success(res.data.message || 'Transformation applied');
      fetchData();
    } catch (err) {
      toast.error('Failed to apply transformation');
    } finally {
      setWranglingLoading(false);
    }
  };

  const formatSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const groupStyle = {
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    marginBottom: '16px',
    overflow: 'hidden'
  };

  const groupHeaderStyle = {
    background: '#f5f7fa',
    padding: '12px 16px',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontWeight: 600,
    fontSize: '14px',
    color: '#2c3e50'
  };

  const groupContentStyle = {
    padding: '16px',
    background: 'white'
  };

  const renderTable = (datasets, showLink = false, linkedIds = []) => (
    <TableContainer sx={{ borderRadius: '4px', border: '1px solid #e0e0e0' }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ background: '#34495e' }}>
            <TableCell sx={{ color: 'white', fontWeight: 500, fontSize: '12px' }}>Actions</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 500, fontSize: '12px' }}>Filename</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 500, fontSize: '12px' }}>Size</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 500, fontSize: '12px' }}>Rows</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 500, fontSize: '12px' }}>Columns</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 500, fontSize: '12px' }}>Uploaded</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {datasets.map((ds) => (
            <React.Fragment key={ds.id}>
              <TableRow hover sx={{ '&:last-child td': { borderBottom: previewTable === ds.id ? 0 : undefined } }}>
                <TableCell>
                  <IconButton onClick={() => handlePreview(ds.id)} size="small" sx={{ color: previewTable === ds.id ? '#e74c3c' : '#3498db' }}>
                    {previewTable === ds.id ? <Close fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                  <IconButton onClick={() => handleOpenWrangling(ds.id, ds.columns_info?.columns || [])} size="small" sx={{ color: '#f39c12' }} title="Data Wrangling">
                    <Build fontSize="small" />
                  </IconButton>
                  {showLink ? (
                    <IconButton 
                      onClick={() => handleLink(ds.id)} 
                      size="small" 
                      color="primary" 
                      disabled={linkedIds.includes(ds.id)}
                      title={linkedIds.includes(ds.id) ? 'Already linked to this project' : 'Link to project'}
                    >
                      <LinkIcon fontSize="small" />
                    </IconButton>
                  ) : (
                    <IconButton onClick={() => handleUnlink(ds.id)} size="small" color="error"><Delete fontSize="small" /></IconButton>
                  )}
                </TableCell>
                <TableCell sx={{ fontSize: '12px', fontWeight: 500 }}>{ds.original_filename}</TableCell>
                <TableCell><Chip label={formatSize(ds.file_size)} size="small" sx={{ fontSize: '10px', background: '#ecf0f1' }} /></TableCell>
                <TableCell sx={{ fontSize: '12px' }}>{ds.row_count?.toLocaleString() || '-'}</TableCell>
                <TableCell sx={{ fontSize: '12px' }}>{ds.column_count || '-'}</TableCell>
                <TableCell sx={{ fontSize: '12px' }}>{new Date(ds.upload_date).toLocaleString()}</TableCell>
              </TableRow>
              {previewTable === ds.id && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ p: 2, background: '#f0f4f8', borderBottom: '2px solid #1abc9c' }}>
                    {previewLoading ? (
                      <Box sx={{ textAlign: 'center' }}>
                        <LinearProgress sx={{ maxWidth: 200, mx: 'auto', mb: 1 }} />
                        <Typography variant="body2" color="textSecondary">Loading preview...</Typography>
                      </Box>
                    ) : previewData ? (
                      <Box>
                        <Typography variant="body2" fontWeight={600} sx={{ mb: 1, color: '#2c3e50' }}>
                          📋 Preview: {ds.original_filename} ({previewData.total_rows?.toLocaleString()} rows)
                        </Typography>
                        <Box sx={{ overflowX: 'auto', maxHeight: 250, border: '1px solid #ddd', borderRadius: '4px', background: 'white' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                            <thead>
                              <tr style={{ background: '#34495e', color: 'white', position: 'sticky', top: 0 }}>
                                <th style={{ padding: '8px 12px', fontWeight: 600, textAlign: 'left', whiteSpace: 'nowrap' }}>#</th>
                                {previewData.columns?.map((col) => (
                                  <th key={col} style={{ padding: '8px 12px', fontWeight: 600, textAlign: 'left', whiteSpace: 'nowrap' }}>{col}</th>
                                ))}
                               </tr>
                            </thead>
                            <tbody>
                              {previewData.rows?.map((row, i) => (
                                <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                                  <td style={{ padding: '6px 12px', color: '#95a5a6' }}>{i + 1}</td>
                                  {previewData.columns?.map((col) => (
                                    <td key={col} style={{ padding: '6px 12px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {row[col] !== null && row[col] !== undefined ? String(row[col]) : '-'}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </Box>
                      </Box>
                    ) : null}
                  </TableCell>
                </TableRow>
              )}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box sx={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: '#2c3e50' }}>
        📊 Project Datasets
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Linked Section */}
      <div style={groupStyle}>
        <div style={{ ...groupHeaderStyle, cursor: 'default' }}>
          <span>🔗 Linked to this project</span>
          <Button startIcon={<Refresh />} onClick={fetchData} size="small" sx={{ fontSize: '11px' }}>Refresh</Button>
        </div>
        <div style={groupContentStyle}>
          {loading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
          ) : linked.length === 0 ? (
            <Typography sx={{ py: 2, textAlign: 'center', color: '#95a5a6' }}>No datasets linked to this project. Use the "Public library" tab below to link one.</Typography>
          ) : (
            renderTable(linked, false, [])
          )}
        </div>
      </div>

      {/* Public Library Section */}
      <div style={groupStyle}>
        <div style={{ ...groupHeaderStyle, cursor: 'default' }}>
          <span>📚 Public library</span>
          <Button startIcon={<Refresh />} onClick={fetchData} size="small" sx={{ fontSize: '11px' }}>Refresh</Button>
        </div>
        <div style={groupContentStyle}>
          {loading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
          ) : publicUploads.length === 0 ? (
            <Typography sx={{ py: 2, textAlign: 'center', color: '#95a5a6' }}>No public datasets available. Upload files in "Import & Data".</Typography>
          ) : (
            renderTable(publicUploads, true, linked.map(l => l.id))
          )}
        </div>
      </div>

      {/* Data Wrangling Section (same as TableList) */}
      <div style={{ ...groupStyle, marginTop: '24px' }}>
        <div style={groupHeaderStyle} onClick={() => setShowWrangling(!showWrangling)}>
          <span>🔧 Data Wrangling</span>
          <span>{showWrangling ? <ExpandLess /> : <ExpandMore />}</span>
        </div>
        <Collapse in={showWrangling}>
          <div style={groupContentStyle}>
            <Typography variant="body2" sx={{ mb: 2, color: '#7f8c8d' }}>
              Select a linked dataset and apply transformations. Changes are saved to the original file.
            </Typography>
            <Box sx={{ mb: 2, maxWidth: 400 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Select Table</InputLabel>
                <Select
                  value={wranglingTable || ''}
                  onChange={async (e) => {
                    setWranglingTable(e.target.value);
                    await loadWranglingColumns(e.target.value);
                  }}
                  label="Select Table"
                >
                  <MenuItem value="">-- Choose a table --</MenuItem>
                  {linked.map(t => (
                    <MenuItem key={t.id} value={t.id}>{t.original_filename}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {wranglingTable && (
              <>
                <Box sx={{ mb: 2, maxWidth: 400 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Action</InputLabel>
                    <Select
                      value={wranglingAction}
                      onChange={(e) => setWranglingAction(e.target.value)}
                      label="Action"
                    >
                      <MenuItem value="sort">Sort</MenuItem>
                      <MenuItem value="extract_date">Extract from Date</MenuItem>
                      <MenuItem value="concat_columns">Concat Columns</MenuItem>
                      <MenuItem value="math_operation">Math Operation</MenuItem>
                      <MenuItem value="uppercase">UPPERCASE Column</MenuItem>
                      <MenuItem value="lowercase">lowercase Column</MenuItem>
                      <MenuItem value="round">Round Numbers</MenuItem>
                      <MenuItem value="fill_na">Fill Missing Values</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <Box sx={{ p: 2, background: '#f9f9f9', borderRadius: '8px', mb: 2 }}>
                  {wranglingAction === 'sort' && (
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Column</InputLabel>
                        <Select
                          value={wranglingConfig.sortCol}
                          onChange={(e) => setWranglingConfig({...wranglingConfig, sortCol: e.target.value})}
                          label="Column"
                        >
                          <MenuItem value="">-- Select --</MenuItem>
                          {wranglingColumns.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Direction</InputLabel>
                        <Select
                          value={wranglingConfig.sortDir}
                          onChange={(e) => setWranglingConfig({...wranglingConfig, sortDir: e.target.value})}
                          label="Direction"
                        >
                          <MenuItem value="asc">Ascending</MenuItem>
                          <MenuItem value="desc">Descending</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  )}

                  {wranglingAction === 'extract_date' && (
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Date Column</InputLabel>
                        <Select
                          value={wranglingConfig.dateCol}
                          onChange={(e) => setWranglingConfig({...wranglingConfig, dateCol: e.target.value})}
                          label="Date Column"
                        >
                          <MenuItem value="">-- Select --</MenuItem>
                          {wranglingColumns.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Extract</InputLabel>
                        <Select
                          value={wranglingConfig.extractPart}
                          onChange={(e) => setWranglingConfig({...wranglingConfig, extractPart: e.target.value})}
                          label="Extract"
                        >
                          <MenuItem value="year">Year</MenuItem>
                          <MenuItem value="month">Month</MenuItem>
                          <MenuItem value="quarter">Quarter</MenuItem>
                          <MenuItem value="day">Day</MenuItem>
                          <MenuItem value="weekday">Weekday</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        size="small"
                        label="New Column Name"
                        value={wranglingConfig.newColName}
                        onChange={(e) => setWranglingConfig({...wranglingConfig, newColName: e.target.value})}
                        sx={{ minWidth: 150 }}
                      />
                    </Box>
                  )}

                  {wranglingAction === 'concat_columns' && (
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Column A</InputLabel>
                        <Select
                          value={wranglingConfig.col1}
                          onChange={(e) => setWranglingConfig({...wranglingConfig, col1: e.target.value})}
                          label="Column A"
                        >
                          <MenuItem value="">-- Select --</MenuItem>
                          {wranglingColumns.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                        </Select>
                      </FormControl>
                      <TextField
                        size="small"
                        label="Separator"
                        value={wranglingConfig.separator}
                        onChange={(e) => setWranglingConfig({...wranglingConfig, separator: e.target.value})}
                        sx={{ width: 80 }}
                      />
                      <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Column B</InputLabel>
                        <Select
                          value={wranglingConfig.col2}
                          onChange={(e) => setWranglingConfig({...wranglingConfig, col2: e.target.value})}
                          label="Column B"
                        >
                          <MenuItem value="">-- Select --</MenuItem>
                          {wranglingColumns.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                        </Select>
                      </FormControl>
                      <TextField
                        size="small"
                        label="New Column Name"
                        value={wranglingConfig.newColName}
                        onChange={(e) => setWranglingConfig({...wranglingConfig, newColName: e.target.value})}
                        sx={{ minWidth: 150 }}
                      />
                    </Box>
                  )}

                  {wranglingAction === 'math_operation' && (
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Column A</InputLabel>
                        <Select
                          value={wranglingConfig.col1}
                          onChange={(e) => setWranglingConfig({...wranglingConfig, col1: e.target.value})}
                          label="Column A"
                        >
                          <MenuItem value="">-- Select --</MenuItem>
                          {wranglingColumns.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ width: 80 }}>
                        <InputLabel>Op</InputLabel>
                        <Select
                          value={wranglingConfig.operator || '+'}
                          onChange={(e) => setWranglingConfig({...wranglingConfig, operator: e.target.value})}
                          label="Op"
                        >
                          <MenuItem value="+">+</MenuItem>
                          <MenuItem value="-">-</MenuItem>
                          <MenuItem value="*">*</MenuItem>
                          <MenuItem value="/">/</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Column B</InputLabel>
                        <Select
                          value={wranglingConfig.col2}
                          onChange={(e) => setWranglingConfig({...wranglingConfig, col2: e.target.value})}
                          label="Column B"
                        >
                          <MenuItem value="">-- Select --</MenuItem>
                          {wranglingColumns.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                        </Select>
                      </FormControl>
                      <TextField
                        size="small"
                        label="New Column Name"
                        value={wranglingConfig.newColName}
                        onChange={(e) => setWranglingConfig({...wranglingConfig, newColName: e.target.value})}
                        sx={{ minWidth: 150 }}
                      />
                    </Box>
                  )}

                  {(wranglingAction === 'uppercase' || wranglingAction === 'lowercase' || wranglingAction === 'round') && (
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
                      <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Column</InputLabel>
                        <Select
                          value={wranglingConfig.col1}
                          onChange={(e) => setWranglingConfig({...wranglingConfig, col1: e.target.value})}
                          label="Column"
                        >
                          <MenuItem value="">-- Select --</MenuItem>
                          {wranglingColumns.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                        </Select>
                      </FormControl>
                      {wranglingAction === 'round' && (
                        <TextField
                          size="small"
                          label="Decimals"
                          type="number"
                          value={wranglingConfig.decimals || 2}
                          onChange={(e) => setWranglingConfig({...wranglingConfig, decimals: parseInt(e.target.value)})}
                          sx={{ width: 100 }}
                        />
                      )}
                    </Box>
                  )}

                  {wranglingAction === 'fill_na' && (
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Column</InputLabel>
                        <Select
                          value={wranglingConfig.col1}
                          onChange={(e) => setWranglingConfig({...wranglingConfig, col1: e.target.value})}
                          label="Column"
                        >
                          <MenuItem value="">-- Select --</MenuItem>
                          {wranglingColumns.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                        </Select>
                      </FormControl>
                      <TextField
                        size="small"
                        label="Fill Value"
                        value={wranglingConfig.fillValue || '0'}
                        onChange={(e) => setWranglingConfig({...wranglingConfig, fillValue: e.target.value})}
                      />
                    </Box>
                  )}
                </Box>

                <Button
                  variant="contained"
                  startIcon={<PlayArrow />}
                  onClick={handleWranglingApply}
                  disabled={wranglingLoading}
                  sx={{ background: '#1abc9c', '&:hover': { background: '#16a085' }, fontSize: '12px' }}
                >
                  {wranglingLoading ? 'Applying...' : 'Apply Transformation'}
                </Button>

                {wranglingResult && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    {wranglingResult.message}
                  </Alert>
                )}
              </>
            )}
          </div>
        </Collapse>
      </div>
    </Box>
  );
};

export default ProjectDatasets;
